// Top-level shell for authenticated routes: thin nav strip + nested route outlet.
// The editor itself owns the bulk of the chrome (top bar with draft selector,
// preview toggle, export, etc). This shell only handles cross-page navigation.

import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Sun, FileText, Calendar, User as UserIcon, LogOut, MailPlus, Send, Settings as SettingsIcon, FileSearch, type LucideIcon } from 'lucide-react';
import type { User } from '@shared/types';

interface Props {
  user: User;
  onLogout: () => Promise<void>;
}

export default function AppShell({ user, onLogout }: Props) {
  const loc = useLocation();
  // When inside the editor, we suppress the nav strip so the editor can use
  // the full viewport (it has its own top bar).
  const hideNav = loc.pathname.startsWith('/draft/');

  const isAdmin = user.role === 'admin';
  const tabCount = isAdmin ? 6 : 5;

  return (
    <div className="h-screen flex flex-col">
      {!hideNav && (
        <>
          {/* Desktop nav: full strip with labels. Hidden on phones. */}
          <nav className="hidden sm:flex border-b border-slate-200 bg-white px-4 py-2.5 items-center gap-3 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2 pr-3 border-r border-slate-200 mr-1">
              <div className="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center">
                <Sun size={16} className="text-blue-700" />
              </div>
              <span className="font-display font-semibold text-slate-900 text-sm">Penobscot Morning</span>
            </Link>

            <NavTab to="/" end label="Drafts" icon={FileText} />
            <NavTab to="/digest" label="Digest" icon={FileSearch} />
            <NavTab to="/events" label="Events" icon={Calendar} />
            <NavTab to="/signups" label="Signups" icon={MailPlus} />
            <NavTab to="/sends" label="Sends" icon={Send} />
            {isAdmin && <NavTab to="/settings" label="Settings" icon={SettingsIcon} />}

            <div className="flex-1" />

            <NavLink to="/account" className={({ isActive }) =>
              `flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`
            }>
              <UserIcon size={13} />
              {user.display_name || user.email}
              {isAdmin && <span className="ml-1 text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 rounded">admin</span>}
            </NavLink>
            <button onClick={onLogout} className="hidden sm:inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" title="Sign out">
              <LogOut size={13} />
            </button>
          </nav>

          {/* Mobile top strip: brand + account avatar. Hidden on desktop. */}
          <div className="sm:hidden flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2.5 flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 bg-blue-100 rounded-md flex items-center justify-center">
                <Sun size={16} className="text-blue-700" />
              </div>
              <span className="font-display font-semibold text-slate-900 text-sm">Penobscot Morning</span>
            </Link>
            <NavLink to="/account" className={({ isActive }) =>
              `flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-600'}`
            }>
              <UserIcon size={14} />
              {isAdmin && <span className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 rounded">admin</span>}
            </NavLink>
          </div>
        </>
      )}
      <div className={`flex-1 overflow-hidden ${hideNav ? '' : 'pb-14 sm:pb-0'}`}>
        <Outlet />
      </div>

      {/* Mobile bottom tab bar. Hidden on desktop and inside the editor. */}
      {!hideNav && (
        <nav
          className="sm:hidden fixed bottom-0 inset-x-0 border-t border-slate-200 bg-white grid h-14 z-30"
          style={{ gridTemplateColumns: `repeat(${tabCount}, minmax(0, 1fr))` }}
        >
          <BottomTab to="/" end label="Drafts" icon={FileText} />
          <BottomTab to="/digest" label="Digest" icon={FileSearch} />
          <BottomTab to="/events" label="Events" icon={Calendar} />
          <BottomTab to="/signups" label="Signups" icon={MailPlus} />
          <BottomTab to="/sends" label="Sends" icon={Send} />
          {isAdmin && <BottomTab to="/settings" label="Settings" icon={SettingsIcon} />}
        </nav>
      )}
    </div>
  );
}

function NavTab({ to, label, icon: Icon, end }: { to: string; label: string; icon: LucideIcon; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md ${isActive ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`
      }
    >
      <Icon size={14} />
      {label}
    </NavLink>
  );
}

function BottomTab({ to, label, icon: Icon, end }: { to: string; label: string; icon: LucideIcon; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex flex-col items-center justify-center gap-0.5 text-[11px] ${isActive ? 'text-blue-700' : 'text-slate-500'}`
      }
    >
      <Icon size={20} />
      <span className="leading-none">{label}</span>
    </NavLink>
  );
}
